<template>
  <div class="pf-field" :class="{ 'pf-wide': def.wide, 'pf-invalid': invalid }">
    <label :id="labelId" :for="inputId" class="pf-label">
      <span :class="{ 'pf-label-line': def.oneLine }">{{ def.label }}</span>
      <span v-if="!def.required && !def.disabled" class="pf-optional">Opcional</span>
      <PortalIcon v-if="def.disabled" name="lock" :size="12" class="pf-lock" />
    </label>

    <!-- A value the applicant can't change (the account's identification, the verified email…) reads like the summary. -->
    <div v-if="def.disabled" class="pf-locked" title="Este dato no se puede modificar">
      <span v-if="displayText">{{ displayText }}</span>
      <span v-else class="pf-empty">Sin registrar</span>
    </div>

    <!-- A list of our own (searchable when long) where the browser's <select> would be too small on a phone. -->
    <PortalSelect
      v-else-if="def.type === 'select' && def.custom"
      :id="inputId"
      :options="def.options"
      :value="value"
      :invalid="invalid"
      :disabled="def.inactive"
      :searchable="def.options.length > 10"
      @input="(v) => $emit('input', v)"
      @blur="$emit('blur')"
    />

    <select
      v-else-if="def.type === 'select'"
      :id="inputId"
      class="pf-input pf-select"
      :class="{ 'is-empty': isEmpty, invalid }"
      :value="isEmpty ? '' : String(value)"
      @change="onSelect"
      @blur="$emit('blur')"
    >
      <option value="" disabled>Seleccione…</option>
      <option v-for="opt in def.options" :key="opt.value" :value="String(opt.value)">{{ opt.text }}</option>
    </select>

    <textarea
      v-else-if="def.type === 'textarea'"
      :id="inputId"
      class="pf-input pf-textarea"
      :class="{ invalid }"
      :placeholder="def.placeholder"
      :value="value"
      :name="def.key"
      @input="$emit('input', $event.target.value)"
      @blur="$emit('blur')"
    ></textarea>

    <div
      v-else-if="def.type === 'radio'"
      class="pf-segmented"
      :class="{ invalid }"
      role="radiogroup"
      :aria-labelledby="labelId"
    >
      <button
        v-for="opt in def.options"
        :key="opt.value"
        type="button"
        role="radio"
        class="pf-segment"
        :class="{ selected: isSelected(opt) }"
        :aria-checked="isSelected(opt) ? 'true' : 'false'"
        @click="$emit('input', opt.value)"
      >
        {{ opt.text }}
      </button>
    </div>

    <!-- The "append" slot (e.g. a button) sits to the right of the input. -->
    <div v-else class="pf-input-row">
      <input
        v-if="def.mask"
        :id="inputId"
        v-mask="def.mask"
        class="pf-input"
        :class="{ invalid, 'pf-input-small': def.small }"
        :type="def.type"
        :placeholder="def.placeholder"
        :value="value"
        :autocomplete="def.autocomplete || 'off'"
        :name="def.key"
        @input="$emit('input', $event.target.value)"
        @blur="$emit('blur')"
        @keydown.enter="$emit('enter')"
      />
      <input
        v-else
        :id="inputId"
        class="pf-input"
        :class="{ invalid, 'pf-input-small': def.small }"
        :type="def.type"
        :placeholder="def.placeholder"
        :min="def.min"
        :max="def.max"
        :value="value"
        :autocomplete="def.autocomplete || 'off'"
        :name="def.key"
        @input="$emit('input', $event.target.value)"
        @blur="$emit('blur')"
        @keydown.enter="$emit('enter')"
      />
      <slot name="append" />
    </div>

    <div v-if="def.hint" class="pf-hint">{{ def.hint }}</div>
    <div v-if="invalid && error" class="pf-error">{{ error }}</div>
  </div>
</template>

<script>
import PortalIcon from "./PortalIcon.vue";
import PortalSelect from "./PortalSelect.vue";

// One field of a section edited in place in the portal, laid out like the section's summary (gray label, bold
// value) but with the value in a soft input. Takes the wizard's field definitions (see NewApplicationForm def());
// a disabled one is shown as plain text.
export default {
  name: "PortalField",
  components: { PortalIcon, PortalSelect },
  props: {
    def: { type: Object, required: true },
    value: { default: "" },
    error: { type: String, default: "" },
    invalid: { type: Boolean, default: false },
  },
  computed: {
    inputId() {
      return "pf-" + this.def.key;
    },
    labelId() {
      return "pf-label-" + this.def.key;
    },
    isEmpty() {
      return this.value === "" || this.value === null || this.value === undefined;
    },
    // What a locked field reads: an option's text for a select or a radio, otherwise the value itself.
    displayText() {
      if (this.isEmpty) return "";
      const option = this.def.options.find((opt) => String(opt.value) === String(this.value));
      return option ? option.text : String(this.value);
    },
  },
  methods: {
    // The option's own value goes back (a number stays a number), not the <select>'s string.
    onSelect(e) {
      const option = this.def.options.find((opt) => String(opt.value) === e.target.value);
      this.$emit("input", option ? option.value : e.target.value);
    },
    // Saved yes/no answers may come back as booleans or as "true"/"false".
    isSelected(opt) {
      return !this.isEmpty && String(this.value) === String(opt.value);
    },
  },
};
</script>

<style scoped>
.pf-field {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.pf-field.pf-wide {
  grid-column: 1 / -1;
}
.pf-label {
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 6px;
  font: 400 12px "Geist", system-ui, sans-serif;
  color: #8a91a3;
}
/* def.oneLine: the label never wraps (it may reach into the gap beside it). */
.pf-label-line {
  white-space: nowrap;
}
.pf-optional {
  font-size: 11px;
  color: #a7adbb;
}
.pf-optional::before {
  content: "·";
  margin-right: 6px;
}
.pf-lock {
  flex-shrink: 0;
  color: #a7adbb;
}

/* Same type as the summary's values, so a filled-in section reads the same either way. */
.pf-locked {
  display: flex;
  align-items: center;
  min-height: 44px;
  font: 600 15px/1.45 "Geist", system-ui, sans-serif;
  color: #0e1424;
  overflow-wrap: anywhere;
}
.pf-empty {
  font-weight: 400;
  color: #9aa1b1;
}
.pf-input {
  box-sizing: border-box;
  width: 100%;
  height: 44px;
  margin: 0;
  padding: 0 14px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f4f5f8;
  font: 600 15px "Geist", system-ui, sans-serif;
  color: #0e1424;
  transition: background-color 0.15s, border-color 0.15s, box-shadow 0.15s;
}
/* def.small: smaller text, for values that run long (an email address). */
.pf-input.pf-input-small {
  font-size: 13px;
  font-weight: 500;
}
.pf-input::placeholder {
  font-weight: 400;
  color: #a7adbb;
}
.pf-input:hover {
  background: #eef0f4;
}
.pf-input:focus {
  outline: none;
  border-color: #e6b65c;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(230, 182, 92, 0.18);
}
.pf-input.invalid {
  border-color: #f2c0c0;
  background: #fff6f6;
}
.pf-input.invalid:focus {
  border-color: #e5484d;
  box-shadow: 0 0 0 3px rgba(229, 72, 77, 0.14);
}
.pf-textarea {
  height: auto;
  min-height: 96px;
  padding: 12px 14px;
  font-weight: 500;
  line-height: 1.5;
  resize: vertical;
}
.pf-select {
  appearance: none;
  -webkit-appearance: none;
  padding-right: 38px;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238a91a3' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;
  cursor: pointer;
}
.pf-select.is-empty {
  font-weight: 400;
  color: #a7adbb;
}
.pf-select option {
  font-weight: 400;
  color: #0e1424;
}
.pf-input-row {
  display: flex;
  gap: 8px;
}
.pf-input-row .pf-input {
  flex: 1;
  min-width: 0;
}

/* Sí / No as a two-way switch. */
.pf-segmented {
  box-sizing: border-box;
  display: inline-flex;
  align-self: flex-start;
  gap: 4px;
  height: 44px;
  padding: 4px;
  border: 1px solid transparent;
  border-radius: 10px;
  background: #f4f5f8;
}
.pf-segmented.invalid {
  border-color: #f2c0c0;
  background: #fff6f6;
}
.pf-segment {
  min-width: 64px;
  height: 100%;
  padding: 0 16px;
  border: 0;
  border-radius: 7px;
  background: transparent;
  font: 600 14px "Geist", system-ui, sans-serif;
  color: #6b7283;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s, box-shadow 0.15s;
}
.pf-segment:hover:not(.selected) {
  color: #0e1424;
}
.pf-segment.selected {
  background: #fff;
  box-shadow: 0 1px 2px rgba(10, 16, 32, 0.08), 0 0 0 1px #e3e6ed;
  color: #0e1424;
}
.pf-segment:focus {
  outline: none;
}
.pf-segment:focus-visible {
  box-shadow: 0 0 0 2px #e6b65c;
}

.pf-hint {
  margin-top: 6px;
  font: 400 12px/1.4 "Geist", system-ui, sans-serif;
  color: #9aa1b1;
}
.pf-error {
  margin-top: 6px;
  font: 500 12px/1.4 "Geist", system-ui, sans-serif;
  color: #c42b2b;
}
</style>
